import { css } from '@emotion/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import Title from 'components/Title/Title';
import Page from 'components/Page/Page';

import fetchNodes from 'store/fetchNodes';
import useNodesAsync from 'store/useNodesAsync';

import NodeTable from './NodeTable';
import StartWorkerDialog from './StartWorkerDialog';

export default function NodesPage() {
  const nodesAsync = useNodesAsync();
  const [dialogNodeId, setDialogNodeId] = useState<string | null>(null);
  const [provisioningNodeId, setProvisioningNodeId] = useState<string | null>(
    null,
  );
  const baselineWorkerCount = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startPolling = useCallback((intervalMs: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchNodes, intervalMs);
  }, []);

  const nudge = useCallback(() => {
    fetchNodes();
    startPolling(3000);
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => startPolling(15000), 30000);
  }, [startPolling]);

  useEffect(() => {
    fetchNodes();
    startPolling(15000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, [startPolling]);

  // 30s timeout to clear provisioning banner
  useEffect(() => {
    if (!provisioningNodeId) return;
    const timer = setTimeout(() => setProvisioningNodeId(null), 30000);
    return () => clearTimeout(timer);
  }, [provisioningNodeId]);

  // Clear provisioning banner when worker count increases
  useEffect(() => {
    if (
      !provisioningNodeId ||
      baselineWorkerCount.current === null ||
      (nodesAsync.status !== 'SUCCESS' && nodesAsync.status !== 'REFRESH')
    )
      return;
    const node = nodesAsync.data.find((n) => n.id === provisioningNodeId);
    if (
      node &&
      (node.worker_summaries?.length ?? 0) > baselineWorkerCount.current
    ) {
      setProvisioningNodeId(null);
    }
  }, [nodesAsync, provisioningNodeId]);

  const handleWorkerSubmit = useCallback(() => {
    if (
      !dialogNodeId ||
      (nodesAsync.status !== 'SUCCESS' && nodesAsync.status !== 'REFRESH')
    )
      return;
    const node = nodesAsync.data.find((n) => n.id === dialogNodeId);
    baselineWorkerCount.current = node?.worker_summaries?.length ?? 0;
    setProvisioningNodeId(dialogNodeId);
    setDialogNodeId(null);
    nudge();
  }, [dialogNodeId, nodesAsync, nudge]);

  return (
    <Fragment>
      <Title>Nodes | Tailor</Title>
      <Page>
        <div
          css={css`
            margin: 48px;

            @media (max-width: 767px) {
              margin: 12px;
            }
          `}
        >
          <Typography variant="h5" sx={{ mb: 1 }}>
            Nodes
          </Typography>

          {(nodesAsync.status === 'INIT' ||
            nodesAsync.status === 'PENDING') && <CircularProgress />}

          {nodesAsync.status === 'ERROR' && (
            <Typography color="error">
              Failed to load node agents. Please try again.
            </Typography>
          )}

          {(nodesAsync.status === 'SUCCESS' ||
            nodesAsync.status === 'REFRESH') && (
            <NodeTable
              nodes={nodesAsync.data}
              onStartWorker={(nodeId) => setDialogNodeId(nodeId)}
              provisioningNodeId={provisioningNodeId}
              onMutate={nudge}
            />
          )}
        </div>

        <StartWorkerDialog
          open={dialogNodeId !== null}
          nodeAgentId={dialogNodeId}
          workdirPackages={
            dialogNodeId && (nodesAsync.status === 'SUCCESS' || nodesAsync.status === 'REFRESH')
              ? Object.fromEntries(
                  (nodesAsync.data
                    .find((n) => n.id === dialogNodeId)
                    ?.worker_summaries || [])
                    .filter((ws) => ws.workdir_name && ws.package_name)
                    .map((ws) => [ws.workdir_name as string, ws.package_name as string]),
                )
              : {}
          }
          onClose={() => setDialogNodeId(null)}
          onSubmit={handleWorkerSubmit}
        />
      </Page>
    </Fragment>
  );
}
